import TopLoader from 'components/TopLoader'
import { LayoutConfig } from 'config/layout'
import NotificationBar from 'layout/Components/NotificationBar'
import React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import HunnyTooltip from 'UIKit/Tooltip'

import { SearchView } from 'layout/Components/AppSearch'
import BottomBar from 'layout/Components/BottomBar'
import Notification from 'layout/Components/Notification'
import Topbar from 'layout/Components/Topbar'
import OpenEffect from 'UIKit/OpenEffect'
import { BAR_LEVEL, CONTENT_LEVEL, SIDEBAR_LEVEL } from './config'
import MainContent from './MainContent'
import MainMenu from './MainMenu'
import MainSidebar from './MainSidebar'

const MainTopbar: React.FC<{ layoutConfig?: LayoutConfig } & BoxProps> = ({ layoutConfig, ...props }) => {
  return (
    <Topbar
      display={[
        !layoutConfig?.header?.mobile?.hidden ? 'block' : 'none',
        ,
        !layoutConfig?.header?.tablet?.hidden ? 'block' : 'none',
        ,
        !layoutConfig?.header?.desktop?.hidden ? 'block' : 'none',
      ]}
      logoDisplayConfig={{
        tablet: {
          hidden: layoutConfig?.menu?.tablet ? !layoutConfig.menu.tablet.hidden : true,
        },
        desktop: {
          hidden: layoutConfig?.menu?.desktop ? !layoutConfig.menu.desktop.hidden : true,
        },
      }}
      {...props}
    />
  )
}

const LayoutContainer: React.FC<{ layoutConfig?: LayoutConfig; children: React.ReactNode }> = ({
  layoutConfig,
  children,
}) => {
  return (
    <OpenEffect openType="grow" height="100%">
      <TopLoader />
      <NotificationBar />

      <StyledLayoutContainer id="main-layout">
        {children}

        <StyledBackground
          display={[
            layoutConfig?.meshBackground?.mobile?.visible ? 'block' : 'none',
            ,
            layoutConfig?.meshBackground?.tablet?.visible ? 'block' : 'none',
            ,
            layoutConfig?.meshBackground?.desktop?.visible ? 'block' : 'none',
          ]}
        />

        <HunnyTooltip id="common-tip-left" place="left" />
        <HunnyTooltip id="common-tip-right" place="right" />
        <HunnyTooltip id="common-tip-bottom" place="bottom" />
        <HunnyTooltip id="common-tip-top" place="top" />
      </StyledLayoutContainer>
    </OpenEffect>
  )
}

const MainLayout: React.FC<
  BoxProps & {
    enabledMeshBackground?: boolean
    htmlContent?: string
    layoutConfig?: LayoutConfig
  }
> = ({ children, htmlContent, maxWidth, layoutConfig, ...props }) => {
  return (
    <LayoutContainer layoutConfig={layoutConfig}>
      <MainMenu config={layoutConfig?.menu} />
      <SearchView />

      <Box minWidth="0" width="100%">
        <MainTopbar layoutConfig={layoutConfig} zIndex={BAR_LEVEL} />

        {/* Main layout: content left, sidebar right */}
        <Flex height="100%">
          <MainContent layoutConfig={layoutConfig} htmlContent={htmlContent} maxWidth={maxWidth} {...props}>
            {children}
          </MainContent>

          <MainSidebar config={layoutConfig?.sideBar} zIndex={SIDEBAR_LEVEL} />
        </Flex>

        <Notification zIndex={CONTENT_LEVEL} />

        <BottomBar
          display={[
            layoutConfig?.bottomBar?.mobile?.hidden ? 'none !important' : 'fixed',
            ,
            layoutConfig?.bottomBar?.tablet?.hidden ? 'none !important' : 'fixed',
            '',
          ]}
          zIndex={BAR_LEVEL}
        />
      </Box>
    </LayoutContainer>
  )
}

MainLayout.defaultProps = {
  width: '100%',
  maxWidth: `${theme.siteWidth}px`,
}

const StyledLayoutContainer = styled(Flex)`
  width: 100%;
  max-width: 100vw;
  height: 100%;
  position: relative;
`

const StyledBackground = styled(Box)`
  position: fixed;
  pointer-events: none;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background-color: transparent;
  background-image: radial-gradient(at 82% 100%, hsla(244, 87%, 33%, 0.5) 10%, transparent 40%),
    radial-gradient(at 10% 50%, hsl(273.92deg 79.21% 27.32% / 52%) 7%, transparent 70%);
`

export default MainLayout
